import { apiFetch, serviceBase } from './http';
import type { CarePlan } from './plan';

/* СНИМКИ ФОТОКОНТРОЛЯ — `document-api`, группы файлов.

       POST {gw}/document-api.application/api/v1/read/groups
       POST   {gw}/document-api.application/api/v1/write/groups/create
       POST   {gw}/document-api.application/api/v1/write/groups/post_care_photo/create
       DELETE {gw}/document-api.application/api/v1/write/groups/element

   ФАЙЛЫ ПОСТУХОДА ЖИВУТ НЕ В ПОСТУХОДЕ. У `post-care-api` ручки приёма снимков
   нет ни одной и не будет: файлы — домен `document-api`, а план знает о них
   ровно одно, число `need` на этапе («врач просит 2 фото»). Поэтому кабинет
   ходит в ДВА сервиса на одном экране, и связывает их не сервер, а он сам.

   ДЕРЕВО ГРУПП, И ПОЧЕМУ ОНО ИМЕННО ТАКОЕ

       корень ─ визит или план ─ externalId = appointmentId либо channelId
         └── день ─ фототочка этапа ─ externalId = id ЭТАПА
               └── файлы ─ type = post_care_photo

   КОРЕНЬ ВЫБИРАЕТСЯ УСЛОВНО, по `plan.appointmentId`, и это не украшение: план
   бывает двух происхождений. Отправленный С ВИЗИТА принадлежит визиту, и его
   снимки обязаны лежать там же, где всё остальное про этот визит, — иначе
   карточка визита и план соберут две разные папки про один приём. Отправленный
   ИЗ КАРТЫ визита не имеет вовсе (`appointmentId: null`), и корнем тогда служит
   сам план: выдумывать ему визит значило бы соврать про происхождение.

   ДЕНЬ — ОТДЕЛЬНАЯ ГРУППА, а не просто `externalId` у файлов. Адресовать файлы
   одним лишь `externalId = <id этапа>` технически можно (`GET /read/{type}` это
   умеет), но тогда снимки разных дней одного плана не собираются в одно место
   ни одним запросом: у них нет общего родителя. Дерево даёт ровно это — один
   запрос к корню возвращает все дни со всеми их снимками.

   `externalId` У ФАЙЛА ТОЖЕ СТАВИТСЯ, и тем же id этапа: так велит модель
   (`files.file.external_id` для `post_care_photo` — это id
   `post_care.channel_stage`), и по нему снимки находятся и без дерева.

   ГРУППЫ НЕ ИЩУТСЯ ПО КЛЮЧУ НА СЕРВЕРЕ. Обе ручки записи ВСЕГДА создают, и это
   решение сервиса, а не недосмотр: ручка, которая молча вернула бы чужую
   группу, неотличима для звонящего от той, что создала свою. Значит «найти или
   создать» собирает КЛИЕНТ — чтением дерева перед записью, и ровно это делает
   `ensure` ниже. Цена названа: две вкладки, жмущие «загрузить» одновременно на
   плане без единого снимка, создадут два корня. Читатель их объединяет (ручка
   чтения складывает группы с одним ключом), поэтому снимки не теряются — но
   дубль реален, и лечится он не здесь, а тем, что загрузка идёт по одной. */

/** Вид КОРНЕВОЙ группы и её внешний id — целиком из происхождения плана. */
export function planRoot(plan: CarePlan): { type: string; externalId: string } {
    return plan.appointmentId
        ? { type: 'appointment', externalId: plan.appointmentId }
        : { type: 'postcare', externalId: plan.id };
}

/** Вид группы одного ДНЯ фотоконтроля. */
const DAY_TYPE = 'post_care_days';
/** Вид самих файлов — `main.type_file`, не `type_file_group`. */
const PHOTO_TYPE = 'post_care_photo';

export type StagePhoto = {
    id: string;
    /** Презигнованная ссылка на объект. ЖИВЁТ МИНУТЫ и обновляется только
     *  перечитыванием дерева — поэтому она не кешируется нигде дольше, чем до
     *  следующего `readStagePhotos`. */
    url: string;
    name: string;
    /** Группа ДНЯ, в которой лежит снимок. Нужна, чтобы его оттуда вынуть:
     *  удаление адресуется парой (группа, файл), а не одним файлом — тот же
     *  файл может лежать и в другом наборе. */
    groupId: string;
};

/** Снимки по id ЭТАПА. Пустая карта — законный ответ: снимков ещё нет. */
export type StagePhotos = Record<string, StagePhoto[]>;

export async function readStagePhotos(plan: CarePlan): Promise<StagePhotos> {
    const base = serviceBase('document-api');
    if (base === null) return {};
    const root = planRoot(plan);
    try {
        const res = await apiFetch(base, '/read/groups', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify([
                { groupType: root.type, externalIds: [root.externalId] },
            ]),
        });
        if (!res.ok) return {};
        return collect(await res.json());
    } catch {
        return {};
    }
}

/** Загрузить один снимок на этап. `true` — сервер принял.
 *
 *  ВОЗВРАЩАЕТСЯ ПРИЗНАК, А НЕ СНИМОК, и это следствие превью: ручка загрузки
 *  отвечает идентификаторами, ссылки на объект в её ответе нет — а без ссылки
 *  нарисовать кадр нечем. Собрать «снимок без url» и дорисовать его в список
 *  значило бы держать в состоянии запись, которую нельзя показать. Экран вместо
 *  этого перечитывает дерево после отправки: один лишний запрос, зато на экране
 *  ровно то, что лежит на сервере. */
export async function uploadStagePhoto(
    plan: CarePlan,
    stageId: string,
    file: File,
): Promise<boolean> {
    const base = serviceBase('document-api');
    if (base === null) return false;
    try {
        const dayId = await ensureDay(base, plan, stageId);
        if (dayId === null) return false;

        const body = new FormData();
        body.append('file', file);
        body.append('externalId', stageId);
        body.append('groupId', dayId);
        // Content-Type НЕ ставим: границу multipart проставляет браузер, и
        // заданный вручную заголовок её потеряет.
        const res = await apiFetch(base, `/write/groups/${PHOTO_TYPE}/create`, {
            method: 'POST',
            body,
        });
        return res.ok;
    } catch {
        return false;
    }
}


/** Вынуть снимок из группы дня. `true` — сервер принял.
 *
 *  Режется СВЯЗКА, а не файл: так устроена ручка, и плану этого достаточно —
 *  снимок с экрана исчезает. Полного удаления файла в сервисе нет вовсе, и
 *  обещать его кнопкой нельзя. */
export async function removeStagePhoto(photo: StagePhoto): Promise<boolean> {
    const base = serviceBase('document-api');
    if (base === null) return false;
    try {
        const query =
            `?groupId=${encodeURIComponent(photo.groupId)}` +
            `&fileId=${encodeURIComponent(photo.id)}`;
        const res = await apiFetch(base, `/write/groups/element${query}`, {
            method: 'DELETE',
        });
        return res.ok;
    } catch {
        return false;
    }
}

/** id группы ДНЯ: найденной в дереве либо созданной вместе с корнем. */
async function ensureDay(
    base: string,
    plan: CarePlan,
    stageId: string,
): Promise<string | null> {
    const root = planRoot(plan);
    const res = await apiFetch(base, '/read/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([
            { groupType: root.type, externalIds: [root.externalId] },
        ]),
    });
    if (!res.ok) return null;

    const buckets = list(await res.json());
    const roots = list(obj(buckets[0]).groups);
    const found = roots.length ? obj(roots[0]) : null;

    // Корня нет — плану ещё ни одного снимка не присылали.
    const rootId = found
        ? str(found.id)
        : await createGroup(base, {
              groupType: root.type,
              externalId: root.externalId,
              name: plan.title,
          });
    if (!rootId) return null;

    // День ищем СРЕДИ ДЕТЕЙ КОРНЯ по `externalId`, а не по имени: имя пациенту
    // видно и может повторяться, id этапа — нет.
    const day = found
        ? list(found.groups).map(obj).find((g) => str(g.externalId) === stageId)
        : undefined;
    if (day) return str(day.id);

    return createGroup(base, {
        groupType: DAY_TYPE,
        externalId: stageId,
        name: dayName(plan, stageId),
        motherGroupId: rootId,
    });
}

async function createGroup(
    base: string,
    payload: Record<string, unknown>,
): Promise<string | null> {
    const res = await apiFetch(base, '/write/groups/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    if (!res.ok) return null;
    const id = str(obj(await res.json()).groupId);
    return id || null;
}

/** Подпись группы дня — «день 3». Только для человека, читающего файлы со
 *  стороны `document-api`: кабинет рисует день из самого плана. */
function dayName(plan: CarePlan, stageId: string): string {
    const stage = plan.stages.find((s) => s.id === stageId);
    return stage ? `день ${stage.day}` : 'фотоконтроль';
}

/** Дерево ответа → снимки по id этапа. Читается ВТОРОЙ уровень: у корня своих
 *  файлов нет и быть не должно, снимки лежат в днях. */
function collect(raw: unknown): StagePhotos {
    const out: StagePhotos = {};
    for (const bucket of list(raw)) {
        for (const root of list(obj(bucket).groups)) {
            for (const day of list(obj(root).groups)) {
                const d = obj(day);
                const stageId = str(d.externalId);
                if (!stageId) continue;
                const dayId = str(d.id);
                out[stageId] = list(d.data).map((f) => {
                    const file = obj(f);
                    return {
                        id: str(file.id),
                        url: str(file.url),
                        name: str(file.name) || str(file.originalName),
                        groupId: dayId,
                    };
                });
            }
        }
    }
    return out;
}

const obj = (v: unknown): Record<string, unknown> => (v ?? {}) as Record<string, unknown>;
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
